import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { db } from "../firebase/config";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import SidekickBanner from "../components/SidekickBanner";

export default function Home() {
  const { currentUser, userProfile, logout } = useAuth();
  const theme = useTheme();
  const [books, setBooks] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) return;
    const q = query(
      collection(db, "books"),
      where("userId", "==", currentUser.uid)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const bookList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));
      setBooks(bookList);
    });
    return unsubscribe;
  }, [currentUser]);

  function getRecast(book) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const pagesRead = book.pagesRead || 0;
    const remaining = book.totalPages - pagesRead;
    if (remaining <= 0) return { message: "✅ Finished!", color: "#48bb78" };
    if (book.targetDate) {
      const target = new Date(book.targetDate);
      target.setHours(0, 0, 0, 0);
      const daysLeft = Math.ceil((target - today) / (1000 * 60 * 60 * 24));
      if (daysLeft <= 0) return { message: "⚠️ Past due date!", color: "#f56565" };
      const pagesPerDay = Math.ceil(remaining / daysLeft);
      const onTrack = book.dailyTarget ? pagesPerDay <= book.dailyTarget : true;
      return {
        message: `📖 Read ${pagesPerDay} pages/day to finish on time`,
        subMessage: `${daysLeft} days left · ${remaining} pages remaining`,
        color: onTrack ? "#48bb78" : "#ed8936"
      };
    }
    if (book.sessions && book.sessions.length > 0) {
      const avgPages = pagesRead / book.sessions.length;
      const daysToFinish = Math.ceil(remaining / avgPages);
      const projectedDate = new Date();
      projectedDate.setDate(projectedDate.getDate() + daysToFinish);
      return {
        message: `📅 At your pace, done by ${projectedDate.toLocaleDateString()}`,
        subMessage: `Avg ${Math.round(avgPages)} pages/session · ${remaining} pages remaining`,
        color: "#4299e1"
      };
    }
    return {
      message: `📚 ${remaining} pages to go — set a goal to get started!`,
      color: "#a0aec0"
    };
  }

  // Returns alert counts for a single book — used for both the per-card
  // badge and the top summary banner. Conditions match BookDetail.js exactly.
  function getQuizAlerts(book) {
    const quizzes = book.quizzes || [];
    const reviseCount = quizzes.filter((q) => q.needsRevision).length;
    const newFeedbackCount = quizzes.filter(
      (q) => q.reviewedByParent && q.feedbackSeenByChild !== true
    ).length;
    return { reviseCount, newFeedbackCount, total: reviseCount + newFeedbackCount };
  }

  // Total alerts across all books — drives the top banner
  const totalAlerts = books.reduce((sum, b) => sum + getQuizAlerts(b).total, 0);

  const styles = {
    container: { minHeight: "100vh", backgroundColor: theme.bgPage, position: "relative" },
    content: { padding: 24, maxWidth: 800, margin: "0 auto" },
    topRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 },
    sectionTitle: { fontSize: 20, color: theme.colorText, margin: 0, fontFamily: theme.fontDisplay, fontSize: 14 },
    addButton: { padding: "10px 20px", backgroundColor: theme.colorPrimary, color: "white", border: "none", borderRadius: 12, fontSize: 14, cursor: "pointer", fontFamily: theme.fontBody, fontWeight: "bold" },
    emptyState: { textAlign: "center", padding: 60 },
    emptyText: { fontSize: 20, color: theme.colorText, fontFamily: theme.fontBody },
    emptySubtext: { fontSize: 14, color: theme.colorTextSoft, fontFamily: theme.fontBody },
    bookGrid: { display: "flex", flexDirection: "column", gap: 16 },
    bookCard: { backgroundColor: theme.bgCard, borderRadius: 16, padding: 20, border: `2px solid ${theme.bgCardBorder}`, cursor: "pointer", display: "flex", gap: 16, position: "relative" },
    cover: { width: 80, height: 120, objectFit: "cover", borderRadius: 6 },
    bookInfo: { flex: 1 },
    bookTitle: { fontSize: 16, color: theme.colorText, margin: "0 0 4px 0", fontFamily: theme.fontBody, fontWeight: "bold" },
    bookAuthor: { fontSize: 13, color: theme.colorTextSoft, margin: "0 0 12px 0", fontFamily: theme.fontBody },
    progressBar: { height: 8, backgroundColor: theme.bgPage, borderRadius: 4, marginBottom: 6 },
    progressFill: { height: "100%", backgroundColor: theme.colorPrimary, borderRadius: 4, transition: "width 0.3s ease" },
    progressText: { fontSize: 12, color: theme.colorTextSoft, margin: "0 0 12px 0", fontFamily: theme.fontBody },
    recastBadge: { borderRadius: 8, padding: "10px 14px" },
    recastMessage: { color: "white", fontSize: 13, fontWeight: "bold", margin: 0, fontFamily: theme.fontBody },
    recastSub: { color: "white", fontSize: 11, margin: "4px 0 0 0", opacity: 0.9 },
    section: { marginBottom: 32 },
    sectionLabel: { fontSize: 16, color: theme.colorAccent, margin: "0 0 16px 0", fontWeight: "bold", fontFamily: theme.fontDisplay, letterSpacing: "0.5px" },
    bookMetaText: { fontSize: 13, color: theme.colorTextSoft, margin: "0 0 8px 0", fontFamily: theme.fontBody },
    emptyActive: { backgroundColor: theme.bgCard, borderRadius: 12, padding: 20, textAlign: "center", marginBottom: 16, border: `1px solid ${theme.bgCardBorder}` },
    emptyActiveText: { color: theme.colorTextSoft, fontSize: 15, margin: 0, fontFamily: theme.fontBody },
    quizAlertBanner: { backgroundColor: "#fff5f5", border: "2px solid #fc8181", borderRadius: 12, padding: "14px 18px", marginBottom: 20 },
    quizAlertText: { fontSize: 14, color: "#c53030", fontWeight: "bold", margin: 0, fontFamily: theme.fontBody },
    bookCardBadge: { position: "absolute", top: 12, right: 12, fontSize: 11, fontWeight: "bold", borderRadius: 10, padding: "3px 8px", color: "white", backgroundColor: "#e53e3e" },
  };

  return (
    <div style={styles.container}>
      {/* Header */}
    <Header />
      {/* Content */}
      <div style={styles.content}>
        {userProfile?.role === "child" && <SidekickBanner />}

        {/* Quiz alert banner — student only, shown when any book has pending action */}
        {userProfile?.role === "child" && totalAlerts > 0 && (
          <div style={styles.quizAlertBanner}>
            <p style={styles.quizAlertText}>
              🔔 You have {totalAlerts} quiz{totalAlerts > 1 ? "zes" : ""} that need{totalAlerts === 1 ? "s" : ""} your attention — tap the book below to see more.
            </p>
          </div>
        )}

        <div style={styles.topRow}>
          <h2 style={styles.sectionTitle}>My Books</h2>
          <button style={styles.addButton} onClick={() => navigate("/add-book")}>
            {theme.addBookLabel}
          </button>
          {userProfile?.role === "child" && (
            <button style={{...styles.addButton, backgroundColor: theme.colorAccent, color: "#3d2b00"}} onClick={() => navigate("/sight-word-quiz")}>
              📖 Word Quiz
            </button>
          )}
        </div>

        {books.length === 0 ? (
          <div style={styles.emptyState}>
            <p style={styles.emptyText}>No books yet!</p>
            <p style={styles.emptySubtext}>Tap + Add Book to get started.</p>
          </div>
        ) : (
          <div>
            {/* Currently Reading */}
            {books.filter(b => b.status === "active" && (b.pagesRead || 0) < b.totalPages).length > 0 && (
              <div style={styles.section}>
                <h3 style={styles.sectionLabel}>📖 Currently Reading</h3>
                <div style={styles.bookGrid}>
                  {books.filter(b => b.status === "active" && (b.pagesRead || 0) < b.totalPages).map((book) => {
                    const recast = getRecast(book);
                    const alerts = getQuizAlerts(book);
                    return (
                      <div key={book.id} style={styles.bookCard} onClick={() => navigate(`/book/${book.id}`)}>
                        {alerts.total > 0 && (
                          <span style={styles.bookCardBadge}>
                            {alerts.reviseCount > 0 ? "🔁" : "🏆"} {alerts.total}
                          </span>
                        )}
                        {book.coverUrl && <img src={book.coverUrl} alt={book.title} style={styles.cover} />}
                        <div style={styles.bookInfo}>
                          <h3 style={styles.bookTitle}>{book.title}</h3>
                          <p style={styles.bookAuthor}>{book.author}</p>
                          <div style={styles.progressBar}>
                            <div style={{ ...styles.progressFill, width: `${Math.min(((book.pagesRead || 0) / book.totalPages) * 100, 100)}%` }} />
                          </div>
                          <p style={styles.progressText}>{book.pagesRead || 0} of {book.totalPages} pages</p>
                          <div style={{ ...styles.recastBadge, backgroundColor: recast.color }}>
                            <p style={styles.recastMessage}>{recast.message}</p>
                            {recast.subMessage && <p style={styles.recastSub}>{recast.subMessage}</p>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Up Next / Queue */}
            {books.filter(b => b.status === "queue").length > 0 && (
              <div style={styles.section}>
                <h3 style={styles.sectionLabel}>📚 Up Next</h3>
                <div style={styles.bookGrid}>
                  {books.filter(b => b.status === "queue").map((book) => {
                    const alerts = getQuizAlerts(book);
                    return (
                      <div key={book.id} style={styles.bookCard} onClick={() => navigate(`/book/${book.id}`)}>
                        {alerts.total > 0 && (
                          <span style={styles.bookCardBadge}>
                            {alerts.reviseCount > 0 ? "🔁" : "🏆"} {alerts.total}
                          </span>
                        )}
                        {book.coverUrl && <img src={book.coverUrl} alt={book.title} style={styles.cover} />}
                        <div style={styles.bookInfo}>
                          <h3 style={styles.bookTitle}>{book.title}</h3>
                          <p style={styles.bookAuthor}>{book.author}</p>
                          <p style={styles.bookMetaText}>{book.totalPages} pages</p>
                          <div style={{ ...styles.recastBadge, backgroundColor: "#a0aec0" }}>
                            <p style={styles.recastMessage}>📚 In your queue</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Finished */}
            {books.filter(b => (b.pagesRead || 0) >= b.totalPages).length > 0 && (
              <div style={styles.section}>
                <h3 style={styles.sectionLabel}>✅ Finished</h3>
                <div style={styles.bookGrid}>
                  {books.filter(b => (b.pagesRead || 0) >= b.totalPages).map((book) => {
                    const alerts = getQuizAlerts(book);
                    return (
                      <div key={book.id} style={styles.bookCard} onClick={() => navigate(`/book/${book.id}`)}>
                        {alerts.total > 0 && (
                          <span style={styles.bookCardBadge}>
                            {alerts.reviseCount > 0 ? "🔁" : "🏆"} {alerts.total}
                          </span>
                        )}
                        {book.coverUrl && <img src={book.coverUrl} alt={book.title} style={styles.cover} />}
                        <div style={styles.bookInfo}>
                          <h3 style={styles.bookTitle}>{book.title}</h3>
                          <p style={styles.bookAuthor}>{book.author}</p>
                          <p style={styles.bookMetaText}>{book.totalPages} pages</p>
                          <div style={{ ...styles.recastBadge, backgroundColor: "#48bb78" }}>
                            <p style={styles.recastMessage}>✅ Completed!</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* No active books message */}
            {books.filter(b => b.status === "active" && (b.pagesRead || 0) < b.totalPages).length === 0 &&
             books.filter(b => b.status === "queue").length > 0 && (
              <div style={styles.emptyActive}>
                <p style={styles.emptyActiveText}>No books in progress — pick one from your queue!</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}